import VectorSource from 'ol/source/Vector'
import { Inject, Injectable } from '@angular/core'
import { OpenLayersLayerService } from '../../../../../shared/open-layers/open-layers-layer.service'
import { OpenLayersMapService } from '../../../../../shared/open-layers/open-layers-map.service'
import { OpenLayersGeoJsonService } from '../../../../../shared/open-layers/open-layers-geo-json.service'
import { MapLayerSettingsStorageService } from '../../../../../core/storage/map-layer-settings-storage.service'
import { GeneralMapRandomParamService } from './general-map-random-param.service'
import { Feature, Map } from 'ol'
import { OpenLayersService } from '../../../../../shared/open-layers/open-layers.service'
import { filter, take } from 'rxjs/operators'
import { Store } from '@ngrx/store'
import { GeneralMapService } from './general-map.service'
import VectorLayer from 'ol/layer/Vector'

@Injectable({
    providedIn: 'root',
})
export class GeneralMapLayerService {

    private warnedMissingLayers = new Set<string>()

    constructor(
        @Inject(Store) protected store: Store,
        @Inject(MapLayerSettingsStorageService) private mapLayerSettingsStorageService: MapLayerSettingsStorageService,
        @Inject(GeneralMapRandomParamService) private mapRandomParamService: GeneralMapRandomParamService,
        @Inject(OpenLayersGeoJsonService) private openLayersGeoJsonService: OpenLayersGeoJsonService,
        @Inject(OpenLayersLayerService) protected openLayersLayerService: OpenLayersLayerService,
        @Inject(OpenLayersMapService) private openLayersMapService: OpenLayersMapService,
        @Inject(OpenLayersService) private openLayersService: OpenLayersService,
        @Inject(GeneralMapService) private generalMapService: GeneralMapService,
    ) {
    }

    public addFeaturesToLayer(features: Feature[], layerName: string, mapId: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                const vectorSource = this.resolveVectorSource(layerName, mapId, map[mapId])

                this.openLayersLayerService.addFeaturesToVectorSource(features, vectorSource)
                resolve()
            },error => reject(error))
        })
    }

    public addGeoJsonsToLayer(geoJsons, layerName: string, mapId: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                const vectorSource = this.resolveVectorSource(layerName, mapId, map[mapId])

                if (vectorSource) {
                    this.openLayersGeoJsonService.addGeoJsonFeaturesToVectorSource(geoJsons, vectorSource)
                }
                resolve()
            },error => reject(error))
        })
    }

    public addLayerToMap(layerConfig, mapId: string): Promise<void> {
        return new Promise(resolve => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                if (layerConfig.type) {
                    switch (layerConfig.type) {
                        case 'featureGroup':
                            map[mapId].addLayer(this.generalMapService.getFeatureGroupLayersLayer(mapId, layerConfig.id))
                            break
                        default:
                            const filterObj = this.mapLayerSettingsStorageService.getSavedSettings(layerConfig.id, 'filter')

                            const updatedLayerConfig = {...layerConfig}
                            if (filterObj) {
                                updatedLayerConfig.filter = filterObj
                            }

                            if (this.mapRandomParamService.getMapRandom() === true) {
                                this.mapRandomParamService.addRandomParamToWmsLayerWithFilter(updatedLayerConfig)
                            }

                            const layer = this.openLayersLayerService.buildLayerFromConfig(updatedLayerConfig, true)

                            if (layer) {
                                map[mapId].addLayer(layer)
                            }
                            break
                    }
                }
                resolve()
            })
        })
    }

    public getGeoJsonFromLayer(layerName: string, mapId: string): Promise<string | undefined> {
        return new Promise((resolve, reject) => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                const vectorSource: VectorSource<any> = this.openLayersMapService.getVectorSourceForLayerByName(layerName, map[mapId])

                if (vectorSource) {
                    resolve(this.openLayersGeoJsonService.getGeoJsonFromFeatures(vectorSource.getFeatures()))
                }
                resolve(undefined)
            }, error => reject(error))
        })
    }

    public getLayerFeatures(mapId: string, layerName: string): Promise<Feature<any>[] | undefined> {
        return new Promise((resolve, reject) => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                const vectorSource: VectorSource<any> = this.openLayersMapService.getVectorSourceForLayerByName(layerName, map[mapId])

                if (vectorSource) {
                    resolve(vectorSource.getFeatures())
                }
                resolve(undefined)
            }, error => reject(error))
        })
    }

    /**
     *
     * @param featureId
     * @param layerName
     * @param mapId
     * @param highlightStyle
     */
    public highlightFeatureByIdFromLayerByName(
        featureId: string | number,
        layerName: string,
        mapId: string,
        highlightStyle = 'yellow-semi'
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            this.generalMapService.map$.pipe(
            filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
            take(1), // Execute the code only once
            ).subscribe(map => {
                const vectorSource: VectorSource<any> = this.openLayersMapService.getVectorSourceForLayerByName(layerName, map[mapId])

                vectorSource?.forEachFeature((feature) => {
                    if (feature.get('id') === featureId || featureId === Infinity) {
                        feature.setStyle(this.openLayersService.getColorStyleFromIdentifier(highlightStyle))
                    } else {
                        feature.setStyle(this.openLayersService.getColorStyleFromIdentifier(feature.get('mapColorStyle')))
                    }
                })
                resolve()
            }, error => reject(error))
        })
    }

    /**
     *
     * @param featureId
     * @param layerName
     * @param mapId
     * @param highlightStyle
     */
    public highlightOnlyFeatureByIdFromLayerByName(
        featureId: string | number,
        layerName: string,
        mapId: string,
        highlightStyle = 'yellow-semi'
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                const vectorSource: VectorSource<any> = this.openLayersMapService.getVectorSourceForLayerByName(layerName, map[mapId])
                vectorSource.forEachFeature((feature) => {
                    if (feature.get('id') === featureId || featureId === Infinity) {
                        feature.setStyle(this.openLayersService.getColorStyleFromIdentifier(highlightStyle))
                    }
                })
                resolve()
            }, error => reject(error))
        })
    }

    public setFeatureStyle(
        featureId: string | number,
        layerName: string,
        mapId: string,
        style = 'transparent'
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            this.generalMapService.featureGroupLayers$.pipe(
                // Vrstva (např. hover-overlay) vzniká asynchronně, čekáme na ni, ne jen na registr mapy
                filter(layers => layers[mapId]?.[layerName] !== null && layers[mapId]?.[layerName] !== undefined),
                take(1),
            ).subscribe(layers => {
                const layer: VectorLayer<any> = layers[mapId][layerName]
                const vectorSource = layer.getSource()
                vectorSource.forEachFeature((feature) => {
                    if (feature.getId() === featureId || featureId === Infinity) {
                        feature.setStyle(this.openLayersService.getColorStyleFromIdentifier(style))
                    }
                })
                resolve()
            }, error => reject(error))
        })
    }

    public setGeoJsonsToLayerByName(geoJsons, layerName: string, mapId: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                this.openLayersMapService.removeLayerByName(layerName, map[mapId])

                this.generalMapService.featureGroupLayers$.pipe(
                    filter(layers => layers[mapId] !== null && layers[mapId] !== undefined),
                    take(1),
                ).subscribe(layers => {
                    if (layers[mapId]?.[layerName]) {
                        const vectorSource = this.openLayersMapService.getVectorSourceFromLayer(layers[mapId]?.[layerName])
                        this.openLayersGeoJsonService.addGeoJsonFeaturesToVectorSource(geoJsons, vectorSource)
                        map[mapId].addLayer(layers[mapId]?.[layerName])
                    }
                    resolve()
                })
            }, error => reject(error))
        })
    }

    public setFeaturesToLayerByName(features, layerName: string, mapId: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                this.openLayersMapService.removeLayerByName(layerName, map[mapId])

                this.generalMapService.featureGroupLayers$.pipe(
                    filter(layers => layers[mapId]?.[layerName] !== null && layers[mapId]?.[layerName] !== undefined),
                    take(1), // Execute the code only once
                ).subscribe(() => {
                    const layer = this.generalMapService.getFeatureGroupLayersLayer(mapId, layerName)
                    if (layer) {
                        const vectorSource = this.openLayersMapService.getVectorSourceFromLayer(layer)
                        // @ts-ignore
                        // @TODO: Resolve conflicted types
                        vectorSource.addFeatures(features)
                        map[mapId].addLayer(layer)
                    }
                    resolve()
                })
            }, error => reject(error))
        })
    }

    public removeFeaturesByIdsFromLayerByName(featureIds, layerName: string, mapId: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                const vectorSource = this.resolveVectorSource(layerName, mapId, map[mapId])

                this.openLayersGeoJsonService.removeFeaturesByIds(featureIds, vectorSource)
                resolve()
            }, error => reject(error))
        })
    }

    public removeFeature(feature: Feature, layerName: string, mapId: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                const vectorSource = this.resolveVectorSource(layerName, mapId, map[mapId])

                if (vectorSource) {
                    vectorSource.removeFeature(feature)
                }
                resolve()
            }, error => reject(error))
        })
    }

    public removeAllFeaturesFromLayerByName(layerName: string, mapId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                const vectorSource = this.resolveVectorSource(layerName, mapId, map[mapId])

                this.openLayersGeoJsonService.removeAllFeatures(vectorSource)
                // setSource na té samé instanci vynutí překreslení; když vrstva na mapě není, je to no-op
                this.openLayersMapService.setVectorSourceForLayerByName(layerName, map[mapId], vectorSource)
                resolve(true)
            }, error => reject(error))
        })
    }

    public switchLayerVisibility(layerName: string, mapId: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this.generalMapService.map$.pipe(
                filter(map => map[mapId] !== null && map[mapId] !== undefined), // Check if map is not null or undefined
                take(1), // Execute the code only once
            ).subscribe(map => {
                this.openLayersMapService.switchLayerVisibility(layerName, map[mapId])
                resolve()
            }, error => reject(error))
        })
    }

    /**
     * Najde vector source vrstvy — nejdřív na mapě, pak v registru `featureGroupLayers$`.
     *
     * Vrstvy žijí ve dvou místech: v registru (přežije zavření dialogu, klíčováno konstantním
     * `mapId`) a na samé OL mapě, kam se připínají až při zápisu featur. Mezi tím je okno, ve
     * kterém vrstva existuje, ale na mapě není — čtení „z mapy“ v něm vrátí `undefined`. Dokud se
     * na to sahalo jen přes mapu, operace tiše neudělala nic a přesto resolvnula: v mapě zůstala
     * stará featura nebo se nová nevykreslila, nikde žádná chyba, v Sentry nic (DEV-1707, DEV-2101).
     *
     * Fallback na mapu zůstává první záměrně — vrstvy přidávané přes `addLayerToMap` v registru
     * vůbec nejsou.
     */
    private resolveVectorSource(layerName: string, mapId: string, map: Map): VectorSource<any> {
        const sourceOnMap: VectorSource<any> = this.openLayersMapService.getVectorSourceForLayerByName(layerName, map)

        if (sourceOnMap) {
            return sourceOnMap
        }

        if (this.generalMapService.hasFeatureGroupLayersLayer(mapId, layerName)) {
            const layer = this.generalMapService.getFeatureGroupLayersLayer(mapId, layerName)

            return this.openLayersMapService.getVectorSourceFromLayer(layer) as VectorSource<any>
        }

        this.warnLayerNotFoundOnce(layerName, mapId)

        return null
    }

    /**
     * Není-li vrstva nikde, řekni to — ale jen jednou za kombinaci mapy a vrstvy.
     *
     * Bez logu se tyhle případy hledají jen měřením v prohlížeči; v DEV-1707 to stálo několik hodin.
     * Bez dedupu by to ale byl šum: `BarterMapService.clearLayers` čistí čtyři vrstvy naslepo,
     * takže na čerstvé mapě by warningy padaly při každém volání — a warning, který chodí pořád,
     * si přestane kdokoli číst. První výskyt řekne celý příběh, další už nic nepřidá.
     */
    private warnLayerNotFoundOnce(layerName: string, mapId: string): void {
        const key = `${ mapId }/${ layerName }`

        if (this.warnedMissingLayers.has(key)) {
            return
        }

        this.warnedMissingLayers.add(key)
        console.warn(`Vrstva "${ layerName }" není na mapě "${ mapId }" ani v registru featureGroupLayers, operace se neprovede.`)
    }
}
